import React, { useState, useEffect, useRef, useMemo } from 'react';
 
import './AgencyBillGeneration.css';
import { getVehicles } from '../../services/vehiclesService';
import { getDrivers } from '../../services/driversService';
import api from '../../config/api';

const AgencyBillGeneration = () => {
  // Backend base URL (use env var if provided)
  const API_BASE = process.env.REACT_APP_API_BASE || 'https://transportapi.nettaa.in';

  // Get agency name and ID from localStorage
  const [agencyName, setAgencyName] = useState('Agency Logistics Service');
  const [currentAgencyId, setCurrentAgencyId] = useState(null);
  
  useEffect(() => {
    const savedAgency = localStorage.getItem('agencyProfile');
    if (savedAgency) {
      try {
        const agency = JSON.parse(savedAgency);
        const agencyId = agency.id || agency.agency_id || null;
        setAgencyName(agency.name || 'Agency Logistics Service');
        setCurrentAgencyId(agencyId);
        // set agency id into form data so payload can include it
        setFormData(prev => ({ ...prev, agencyId: agencyId }));
        console.log('Agency Bill Generation: Loaded agency ID:', agencyId, 'Agency Name:', agency.name);
      } catch (error) {
        console.error('Error loading agency profile:', error);
      }
    }
  }, []);

  const [showBillGenerationModal, setShowBillGenerationModal] = useState(false);
  
  const [formData, setFormData] = useState({
    vehicleNumber: '',
    vehicleId: null,
    driverName: '',
    driverId: null,
    driverContactNumber: '',
    clientName: '',
    clientAddress: '',
    clientContact: '',
    consignerName: '',
    consignerAddress: '',
    consignerContact: '',
    consigneeName: '',
    consigneeAddress: '',
    consigneeContact: '',
    fromLocation: '',
    toLocation: '',
    distance: 0,
    agencyId: null,
    tripId: null,
    description: '',
    totalKm: '',
    totalDay: ''
  });

  // Helper to sanitize phone inputs to digits only and max 10
  const sanitizePhone = (val) => {
    if (!val && val !== '') return val;
    const digits = val.replace(/\D/g, '');
    return digits.slice(0, 10);
  };

  const [otherCharges, setOtherCharges] = useState([]);
  const [chargeType, setChargeType] = useState('');
  const [customChargeType, setCustomChargeType] = useState('');
  const [chargeUnit, setChargeUnit] = useState('');
  const [chargeValue, setChargeValue] = useState('');
  const [chargeTypeDropdownOpen, setChargeTypeDropdownOpen] = useState(false);
  const [editingChargeId, setEditingChargeId] = useState(null);
  const [editingBillId, setEditingBillId] = useState(null);

  // Filters for table
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Dropdown states
  const [vehicleNumberDropdownOpen, setVehicleNumberDropdownOpen] = useState(false);
  const [driverNameDropdownOpen, setDriverNameDropdownOpen] = useState(false);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [driverSearch, setDriverSearch] = useState('');
  const [showEntriesDropdownOpen, setShowEntriesDropdownOpen] = useState(false);

  const vehicleNumberDropdownRef = useRef(null);
  const driverNameDropdownRef = useRef(null);
  const showEntriesDropdownRef = useRef(null);
  const fromLocationInputRef = useRef(null);
  const toLocationInputRef = useRef(null);
  const fromLocationSuggestionsRef = useRef(null);
  const toLocationSuggestionsRef = useRef(null);
  const chargeTypeDropdownRef = useRef(null);

  // Vehicles and drivers loaded from backend
  const [vehicles, setVehicles] = useState([]); // array of { id, vehicle_number, assigned_driver_id, ... }
  const [drivers, setDrivers] = useState([]);   // array of { id, name, mobile_number, ... }
  const [isVehiclesLoading, setIsVehiclesLoading] = useState(false);
  const [isDriversLoading, setIsDriversLoading] = useState(false);
  // Success / delete modals
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalType, setSuccessModalType] = useState('add'); // 'add' | 'update'
  const [successBillInfo, setSuccessBillInfo] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [billToDelete, setBillToDelete] = useState(null);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [deletedBillInfo, setDeletedBillInfo] = useState(null);
  const vehicleDebounceRef = useRef(null);
  const driverDebounceRef = useRef(null);
  const originalItemsRef = useRef([]); // for edit sync

  // Derived UI lists
  const vehicleNumberOptions = vehicles.map(v => v.vehicle_number);
  const allDrivers = drivers.map(d => d.name || d.full_name || d.name);
  const driverContactMap = useMemo(() => {
    return drivers.reduce((acc, d) => {
      const name = d.full_name || d.name || '';
      if (name) acc[name] = d.mobile_number || d.contact || d.phone || '';
      return acc;
    }, {});
  }, [drivers]);

  // Server-side fetch helpers (support search)
  const fetchVehicles = async (search = '') => {
    try {
      setIsVehiclesLoading(true);
      const vResp = await getVehicles({ page: 1, limit: 100, search });
      let vehiclesData = [];
      if (vResp?.data && Array.isArray(vResp.data.items)) vehiclesData = vResp.data.items;
      else if (Array.isArray(vResp?.data)) vehiclesData = vResp.data;
      else if (Array.isArray(vResp)) vehiclesData = vResp;
      setVehicles(vehiclesData || []);
    } catch (err) {
      console.error('Failed to load vehicles', err);
      setVehicles([]);
    } finally {
      setIsVehiclesLoading(false);
    }
  };

  const fetchDrivers = async (search = '') => {
    try {
      setIsDriversLoading(true);
      const dResp = await getDrivers({ page: 1, limit: 100, search });
      let driversData = [];
      if (dResp?.data && Array.isArray(dResp.data.items)) driversData = dResp.data.items;
      else if (Array.isArray(dResp?.data)) driversData = dResp.data;
      else if (Array.isArray(dResp)) driversData = dResp;
      setDrivers(driversData || []);
    } catch (err) {
      console.error('Failed to load drivers', err);
      setDrivers([]);
    } finally {
      setIsDriversLoading(false);
    }
  };

  // Initial load: fetch a small page for dropdowns
  const fetchVehicleAndDriverLists = () => {
    fetchVehicles('');
    fetchDrivers('');
  };

  useEffect(() => {
    fetchVehicleAndDriverLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-populate driver when vehicle is selected (if vehicle has assigned driver)
  useEffect(() => {
    if (formData.vehicleNumber) {
      const selectedVehicle = vehicles.find(v => v.vehicle_number === formData.vehicleNumber);
      if (selectedVehicle) {
        const assignedDriverId = selectedVehicle.assigned_driver_id || selectedVehicle.assigned_driver || selectedVehicle.driver_id;
        let driverObj = null;
        if (assignedDriverId) {
          driverObj = drivers.find(d => d.id === assignedDriverId || d.driver_id === assignedDriverId);
        }
        if (!driverObj) {
          const driverNameFromVehicle = selectedVehicle.assigned_driver_name || selectedVehicle.driver_name;
          if (driverNameFromVehicle) {
            driverObj = drivers.find(d => (d.full_name || d.name || '').toLowerCase() === driverNameFromVehicle.toLowerCase());
          }
        }
        if (driverObj) {
      setFormData(prev => ({
        ...prev,
            driverName: driverObj.full_name || driverObj.name || '',
            driverContactNumber: driverObj.mobile_number || driverObj.contact || ''
      }));
        }
      } else {
      setFormData(prev => ({
        ...prev,
        driverName: '',
        driverContactNumber: ''
      }));
    }
    } else {
      setFormData(prev => ({
        ...prev,
        driverName: '',
        driverContactNumber: ''
      }));
    }
  }, [formData.vehicleNumber, vehicles, drivers]);

  // Update driver contact when driver name is manually changed
  useEffect(() => {
    if (formData.driverName) {
      const contact = driverContactMap[formData.driverName] || '';
      setFormData(prev => ({ ...prev, driverContactNumber: contact }));
    } else {
      setFormData(prev => ({ ...prev, driverContactNumber: '' }));
    }
  }, [formData.driverName, driverContactMap]);

  // Indian cities list (all cities from all states)
  const getAllIndianCities = () => {
    const indianStatesAndCities = {
      'Andhra Pradesh': ['Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore', 'Kurnool', 'Rajahmundry', 'Tirupati', 'Kakinada', 'Kadapa', 'Anantapur', 'Eluru', 'Ongole', 'Nandyal', 'Machilipatnam', 'Adoni', 'Tenali', 'Chittoor', 'Hindupur', 'Proddatur', 'Bhimavaram'],
      'Arunachal Pradesh': ['Itanagar', 'Naharlagun', 'Tawang', 'Bomdila', 'Pasighat', 'Tezu', 'Ziro', 'Along', 'Daporijo', 'Namsai', 'Roing', 'Khonsa', 'Aalo', 'Seppa', 'Yingkiong'],
      'Assam': ['Guwahati', 'Silchar', 'Dibrugarh', 'Jorhat', 'Nagaon', 'Tinsukia', 'Tezpur', 'Bongaigaon', 'Dhubri', 'Diphu', 'North Lakhimpur', 'Karimganj', 'Goalpara', 'Barpeta', 'Sivasagar', 'Hailakandi', 'Golaghat', 'Dhekiajuli', 'Mangaldoi'],
      'Bihar': ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Purnia', 'Darbhanga', 'Arrah', 'Bihar Sharif', 'Katihar', 'Munger', 'Chapra', 'Sasaram', 'Hajipur', 'Sitamarhi', 'Samastipur', 'Bettiah', 'Motihari', 'Saharsa', 'Buxar', 'Kishanganj'],
      'Chhattisgarh': ['Raipur', 'Bhilai', 'Bilaspur', 'Durg', 'Korba', 'Rajpur', 'Raigarh', 'Jagdalpur', 'Ambikapur', 'Chirmiri', 'Dhamtari', 'Dantewada', 'Kanker', 'Kawardha', 'Mahasamund', 'Surajpur'],
      'Goa': ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa', 'Ponda', 'Bicholim', 'Valpoi', 'Curchorem', 'Quepem', 'Canacona'],
      'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar', 'Jamnagar', 'Gandhinagar', 'Junagadh', 'Gandhidham', 'Anand', 'Navsari', 'Morbi', 'Nadiad', 'Surendranagar', 'Bharuch', 'Mehsana', 'Bhuj', 'Porbandar', 'Palanpur', 'Valsad'],
      'Haryana': ['Faridabad', 'Gurgaon', 'Panipat', 'Ambala', 'Yamunanagar', 'Rohtak', 'Hisar', 'Karnal', 'Sonipat', 'Panchkula', 'Sirsa', 'Bhiwani', 'Bahadurgarh', 'Jind', 'Thanesar', 'Kaithal', 'Rewari', 'Palwal', 'Fatehabad', 'Hansi'],
      'Himachal Pradesh': ['Shimla', 'Mandi', 'Solan', 'Dharamshala', 'Bilaspur', 'Kullu', 'Chamba', 'Hamirpur', 'Una', 'Kangra', 'Nahan', 'Palampur', 'Sundarnagar', 'Nurpur', 'Keylong'],
      'Jharkhand': ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro Steel City', 'Hazaribagh', 'Deoghar', 'Giridih', 'Adityapur', 'Ramgarh', 'Medininagar', 'Chirkunda', 'Phusro', 'Chaibasa', 'Gumla', 'Lohardaga', 'Dumka', 'Pakur', 'Godda'],
      'Karnataka': ['Bangalore', 'Mysore', 'Hubli', 'Mangalore', 'Belagavi', 'Gulbarga', 'Davangere', 'Bellary', 'Bijapur', 'Shimoga', 'Tumkur', 'Raichur', 'Bidar', 'Hospet', 'Hassan', 'Udupi', 'Gadag', 'Chitradurga', 'Kolar', 'Mandya'],
      'Kerala': ['Kochi', 'Kozhikode', 'Thiruvananthapuram', 'Thrissur', 'Kollam', 'Kannur', 'Alappuzha', 'Kottayam', 'Palakkad', 'Manjeri', 'Thalassery', 'Ponnani', 'Kochi', 'Kayamkulam', 'Kottarakkara', 'Changanassery', 'Kasaragod', 'Pathanamthitta', 'Idukki', 'Wayanad'],
      'Madhya Pradesh': ['Bhopal', 'Indore', 'Gwalior', 'Jabalpur', 'Ujjain', 'Sagar', 'Ratlam', 'Satna', 'Rewa', 'Murwara', 'Singrauli', 'Burhanpur', 'Khandwa', 'Morena', 'Bhind', 'Chhindwara', 'Vidisha', 'Guna', 'Shivpuri', 'Damoh'],
      'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Kalyan', 'Vasai-Virar', 'Navi Mumbai', 'Amravati', 'Nanded', 'Kolhapur', 'Sangli', 'Jalgaon', 'Akola', 'Latur', 'Dhule', 'Ahmednagar', 'Ichalkaranji', 'Jalna', 'Bhusawal', 'Panvel', 'Satara', 'Beed', 'Yavatmal', 'Kamptee', 'Gondia', 'Barshi', 'Achalpur'],
      'Manipur': ['Imphal', 'Thoubal', 'Kakching', 'Ukhrul', 'Bishnupur', 'Churachandpur', 'Senapati', 'Tamenglong', 'Jiribam', 'Moreh', 'Lilong', 'Moirang', 'Mayang Imphal'],
      'Meghalaya': ['Shillong', 'Tura', 'Nongpoh', 'Jowai', 'Nongstoin', 'Baghmara', 'Williamnagar', 'Resubelpara', 'Mankachar', 'Ampati'],
      'Mizoram': ['Aizawl', 'Lunglei', 'Saiha', 'Champhai', 'Kolasib', 'Serchhip', 'Lawngtlai', 'Mamit', 'Khawzawl', 'Hnahthial'],
      'Nagaland': ['Kohima', 'Dimapur', 'Mokokchung', 'Tuensang', 'Wokha', 'Zunheboto', 'Phek', 'Mon', 'Kiphire', 'Longleng', 'Peren'],
      'Odisha': ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Berhampur', 'Sambalpur', 'Puri', 'Baleshwar', 'Bhadrak', 'Baripada', 'Balangir', 'Jharsuguda', 'Bargarh', 'Rayagada', 'Jeypore', 'Dhenkanal', 'Barbil', 'Kendujhar', 'Paradip', 'Bhuban', 'Talcher'],
      'Punjab': ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda', 'Pathankot', 'Hoshiarpur', 'Batala', 'Moga', 'Abohar', 'Khanna', 'Phagwara', 'Muktsar', 'Barnala', 'Firozpur', 'Kapurthala', 'Sultanpur Lodhi', 'Fazilka', 'Gurdaspur', 'Malerkotla'],
      'Rajasthan': ['Jaipur', 'Jodhpur', 'Kota', 'Bikaner', 'Ajmer', 'Udaipur', 'Bhilwara', 'Alwar', 'Bharatpur', 'Sri Ganganagar', 'Sikar', 'Pali', 'Tonk', 'Chittorgarh', 'Hanumangarh', 'Beawar', 'Karauli', 'Dholpur', 'Bundi', 'Baran', 'Churu', 'Jhunjhunu', 'Nagaur', 'Jhalawar', 'Banswara'],
      'Sikkim': ['Gangtok', 'Namchi', 'Mangan', 'Gyalshing', 'Singtam', 'Rangpo', 'Jorethang', 'Pakyong', 'Rhenock', 'Soreng'],
      'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem', 'Tirunelveli', 'Tiruppur', 'Erode', 'Vellore', 'Thoothukudi', 'Dindigul', 'Thanjavur', 'Hosur', 'Nagercoil', 'Kanchipuram', 'Karaikudi', 'Neyveli', 'Cuddalore', 'Kumbakonam', 'Tiruvannamalai', 'Pollachi', 'Rajapalayam', 'Gudiyatham', 'Pudukkottai', 'Ambur'],
      'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Karimnagar', 'Ramagundam', 'Khammam', 'Mahbubnagar', 'Nalgonda', 'Adilabad', 'Suryapet', 'Miryalaguda', 'Siddipet', 'Jagtial', 'Mancherial', 'Kamareddy', 'Kothagudem', 'Bhongir', 'Bodhan', 'Narayanpet', 'Wanaparthy'],
      'Tripura': ['Agartala', 'Udaipur', 'Dharmanagar', 'Kailashahar', 'Belonia', 'Khowai', 'Ambassa', 'Kumarghat', 'Sabroom', 'Teliamura'],
      'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Ghaziabad', 'Agra', 'Meerut', 'Varanasi', 'Allahabad', 'Bareilly', 'Aligarh', 'Moradabad', 'Saharanpur', 'Gorakhpur', 'Noida', 'Firozabad', 'Jhansi', 'Muzaffarnagar', 'Mathura', 'Rampur', 'Shahjahanpur', 'Farrukhabad', 'Fatehpur', 'Budaun', 'Sitapur', 'Hapur', 'Etawah', 'Unnao', 'Pilibhit', 'Lakhimpur', 'Basti', 'Deoria'],
      'Uttarakhand': ['Dehradun', 'Haridwar', 'Roorkee', 'Haldwani', 'Rudrapur', 'Kashipur', 'Rishikesh', 'Nainital', 'Mussoorie', 'Pithoragarh', 'Almora', 'Srinagar', 'Kotdwara', 'Ramnagar', 'Pauri', 'Chamoli Gopeshwar', 'New Tehri', 'Uttarkashi'],
      'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri', 'Bardhaman', 'Malda', 'Baharampur', 'Habra', 'Kharagpur', 'Shantipur', 'Ranaghat', 'Krishnanagar', 'Nabadwip', 'Medinipur', 'Chandannagar', 'Jalpaiguri', 'Baidyabati', 'Darjeeling', 'Purulia', 'Bankura', 'Alipurduar', 'Raiganj'],
      'Delhi': ['New Delhi', 'Delhi Cantonment', 'Narela', 'Najafgarh', 'Rohini', 'Dwarka', 'Patparganj'],
      'Jammu and Kashmir': ['Srinagar', 'Jammu', 'Anantnag', 'Baramulla', 'Sopore', 'Kathua', 'Udhampur', 'Poonch', 'Rajouri', 'Kupwara', 'Bandipora', 'Ganderbal', 'Pulwama', 'Shopian', 'Budgam', 'Kulgam', 'Doda', 'Ramban', 'Kishtwar', 'Reasi'],
      'Ladakh': ['Leh', 'Kargil', 'Drass', 'Nubra Valley', 'Zanskar'],
      'Puducherry': ['Puducherry', 'Karaikal', 'Mahe', 'Yanam'],
      'Andaman and Nicobar Islands': ['Port Blair', 'Diglipur', 'Mayabunder', 'Rangat', 'Car Nicobar'],
      'Chandigarh': ['Chandigarh'],
      'Dadra and Nagar Haveli and Daman and Diu': ['Silvassa', 'Daman', 'Diu'],
      'Lakshadweep': ['Kavaratti', 'Agatti', 'Amini', 'Andrott', 'Kadmat', 'Kalpeni', 'Kiltan', 'Minicoy']
    };
    
    const allCities = [];
    Object.values(indianStatesAndCities).forEach(cities => {
      allCities.push(...cities);
    });
    return [...new Set(allCities)].sort();
  };

  const indianCities = getAllIndianCities();
  const [fromLocationSearch, setFromLocationSearch] = useState('');
  const [toLocationSearch, setToLocationSearch] = useState('');
  const [fromLocationSuggestionsOpen, setFromLocationSuggestionsOpen] = useState(false);
  const [toLocationSuggestionsOpen, setToLocationSuggestionsOpen] = useState(false);

  // Filter cities based on search
  const filteredFromCities = useMemo(() => {
    if (!fromLocationSearch.trim()) return [];
    const searchLower = fromLocationSearch.toLowerCase();
    return indianCities
      .filter(city => city.toLowerCase().includes(searchLower))
      .slice(0, 10); // Limit to 10 suggestions
  }, [fromLocationSearch, indianCities]);

  const filteredToCities = useMemo(() => {
    if (!toLocationSearch.trim()) return [];
    const searchLower = toLocationSearch.toLowerCase();
    return indianCities
      .filter(city => city.toLowerCase().includes(searchLower))
      .slice(0, 10); // Limit to 10 suggestions
  }, [toLocationSearch, indianCities]);

  // Handle from location input
  const handleFromLocationInput = (value) => {
    setFromLocationSearch(value);
    setFormData(prev => ({ ...prev, fromLocation: value }));
    // Show suggestions if there's text and matches exist
    if (value.trim().length > 0) {
      const searchLower = value.toLowerCase();
      const hasMatches = indianCities.some(city => city.toLowerCase().includes(searchLower));
      setFromLocationSuggestionsOpen(hasMatches);
    } else {
      setFromLocationSuggestionsOpen(false);
    }
  };

  // Handle to location input
  const handleToLocationInput = (value) => {
    setToLocationSearch(value);
    setFormData(prev => ({ ...prev, toLocation: value }));
    // Show suggestions if there's text and matches exist
    if (value.trim().length > 0) {
      const searchLower = value.toLowerCase();
      const hasMatches = indianCities.some(city => city.toLowerCase().includes(searchLower));
      setToLocationSuggestionsOpen(hasMatches);
    } else {
      setToLocationSuggestionsOpen(false);
    }
  };

  // Handle from location suggestion select
  const handleFromLocationSelect = (city) => {
    setFormData(prev => ({ ...prev, fromLocation: city }));
    setFromLocationSearch(city);
    setFromLocationSuggestionsOpen(false);
  };

  // Handle to location suggestion select
  const handleToLocationSelect = (city) => {
    setFormData(prev => ({ ...prev, toLocation: city }));
    setToLocationSearch(city);
    setToLocationSuggestionsOpen(false);
  };

  // Start with empty bills array; we'll load from API
  const initialBills = [];
  const [bills, setBills] = useState(initialBills);

  // Fetch bills from backend API
  const fetchBills = async (opts = {}) => {
    try {
      const {
        search = searchQuery,
        payment_status = '',
        customer_id = '',
        agency_id = '',
        trip_id = '',
        start_date = fromDate,
        end_date = toDate,
        limit = itemsPerPage,
        offset = (currentPage - 1) * itemsPerPage,
        order_by = 'bill_date',
        sort_order = 'desc'
      } = opts;

      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (payment_status) params.append('payment_status', payment_status);
      if (customer_id) params.append('customer_id', customer_id);
      if (agency_id) params.append('agency_id', agency_id);
      if (trip_id) params.append('trip_id', trip_id);
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);
      if (limit) params.append('limit', limit);
      if (offset || offset === 0) params.append('offset', offset);
      if (order_by) params.append('order_by', order_by);
      if (sort_order) params.append('sort_order', sort_order);

      const response = await api.get('/api/bills', { params: Object.fromEntries(params.entries()) });
      const json = response.data;
      if (json && json.success && json.data && Array.isArray(json.data.items)) {
        const normalize = (b) => ({
          id: b.bill_number || b.id || '',
          billDate: b.bill_date ? new Date(b.bill_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : (b.billDate || ''),
          fromLocation: b.from_location || b.fromLocation || '',
          toLocation: b.to_location || b.toLocation || '',
          vehicleNumber: b.vehicle_number || b.vehicleNumber || '',
          driverName: b.driver_name || b.driverName || '',
          totalAmount: typeof b.total_amount !== 'undefined' ? Number(b.total_amount) : (b.totalAmount || 0),
          status: b.payment_status || b.status || (b.paymentStatus || 'Pending'),
          agencyId: b.agency_id || b.agencyId || null,
          raw: b
        });
        
        let normalizedBills = json.data.items.map(normalize);
        
        // Apply client-side agency filter as fallback (in case backend doesn't filter by agency_id)
        if (currentAgencyId && normalizedBills.length > 0) {
          const beforeFilter = normalizedBills.length;
          normalizedBills = normalizedBills.filter(b => {
            const billAgencyId = b.agencyId || b.raw?.agency_id;
            const matches = billAgencyId && String(billAgencyId) === String(currentAgencyId);
            if (!matches && billAgencyId) {
              console.log('Agency Bill Generation: Filtered out bill:', b.id, 'agencyId:', billAgencyId, 'expected:', currentAgencyId);
            }
            return matches;
          });
          console.log('Agency Bill Generation: After agency filter:', normalizedBills.length, 'bills (was', beforeFilter, 'before filter)');
        }
        
        setBills(normalizedBills);
      } else {
        setBills([]);
      }
    } catch (err) {
      console.error('Failed to fetch bills', err);
    }
  };

  useEffect(() => {
    // Only fetch bills if we have an agency ID (for agency users)
    if (currentAgencyId !== null) {
      fetchBills({ agency_id: currentAgencyId });
    } else if (currentAgencyId === null) {
      // If agency ID is explicitly null (after checking localStorage), show empty state
      setBills([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, fromDate, toDate, itemsPerPage, currentPage, currentAgencyId]);

  // Distance calculation removed — using explicit consigner/consignee addresses now

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vehicleNumberDropdownRef.current && !vehicleNumberDropdownRef.current.contains(event.target)) {
        setVehicleNumberDropdownOpen(false);
      }
      if (driverNameDropdownRef.current && !driverNameDropdownRef.current.contains(event.target)) {
        setDriverNameDropdownOpen(false);
      }
      if (fromLocationInputRef.current && fromLocationSuggestionsRef.current) {
        if (!fromLocationInputRef.current.contains(event.target) && 
            !fromLocationSuggestionsRef.current.contains(event.target)) {
          setFromLocationSuggestionsOpen(false);
        }
      } else if (fromLocationInputRef.current && !fromLocationInputRef.current.contains(event.target)) {
        setFromLocationSuggestionsOpen(false);
      }
      if (toLocationInputRef.current && toLocationSuggestionsRef.current) {
        if (!toLocationInputRef.current.contains(event.target) &&
            !toLocationSuggestionsRef.current.contains(event.target)) {
          setToLocationSuggestionsOpen(false);
        }
      } else if (toLocationInputRef.current && !toLocationInputRef.current.contains(event.target)) {
        setToLocationSuggestionsOpen(false);
      }
      if (showEntriesDropdownRef.current && !showEntriesDropdownRef.current.contains(event.target)) {
        setShowEntriesDropdownOpen(false);
      }
      if (chargeTypeDropdownRef.current && !chargeTypeDropdownRef.current.contains(event.target)) {
        setChargeTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-hide success modal
  useEffect(() => {
    if (showSuccessModal) {
      const t = setTimeout(() => setShowSuccessModal(false), 3000);
      return () => clearTimeout(t);
    }
  }, [showSuccessModal]);

  // Auto-hide delete success
  useEffect(() => {
    if (showDeleteSuccess) {
      const t = setTimeout(() => setShowDeleteSuccess(false), 3000);
      return () => clearTimeout(t);
    }
  }, [showDeleteSuccess]);

  // Filter bills
  const filteredBills = useMemo(() => {
    return bills.filter(bill => {
      const matchesSearch = searchQuery === '' ||
        bill.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bill.fromLocation.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bill.toLocation.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bill.vehicleNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bill.driverName.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Prefer server's created_at (used by backend filtering) then fall back to bill_date
      const billIsoSource = bill.raw && (bill.raw.created_at || bill.raw.bill_date) ? (bill.raw.created_at || bill.raw.bill_date) : '';
      const billIso = billIsoSource ? new Date(billIsoSource).toISOString().slice(0,10) : '';
      const matchesFromDate = fromDate === '' || (billIso ? billIso >= fromDate : true);
      const matchesToDate = toDate === '' || (billIso ? billIso <= toDate : true);

      return matchesSearch && matchesFromDate && matchesToDate;
    });
  }, [bills, searchQuery, fromDate, toDate]);

  // Pagination
  const totalPages = Math.ceil(filteredBills.length / itemsPerPage) || 1;
  const paginatedBills = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredBills.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredBills, currentPage, itemsPerPage]);

  // Handle charge type selection
  const handleChargeTypeSelect = (type) => {
    setChargeType(type);
    setChargeTypeDropdownOpen(false);
    
    // Auto-set unit label based on selection, but keep field editable
    if (type === 'per km') {
      // Unit field will be editable, user types the quantity
    } else if (type === 'per day') {
      // Unit field will be editable, user types the quantity
    } else if (type === 'Type') {
      setChargeUnit(''); // Allow typing for custom unit
    }
    
    // Clear custom charge type when switching away from "Type"
    if (type !== 'Type') {
      setCustomChargeType('');
    }
    
    // Clear unit, total fields, and charge value when changing charge type
    setChargeUnit('');
    setFormData(prev => ({ ...prev, totalKm: '', totalDay: '' }));
    setChargeValue('');
  };

  // Calculate charge value when unit or total changes (for per km/per day)
  useEffect(() => {
    if (chargeType === 'per km' && chargeUnit && formData.totalKm) {
      const perKmValue = parseFloat(chargeUnit) || 0;
      const totalKmValue = parseFloat(formData.totalKm) || 0;
      const calculatedValue = perKmValue * totalKmValue;
      setChargeValue(calculatedValue.toString());
    } else if (chargeType === 'per day' && chargeUnit && formData.totalDay) {
      const perDayValue = parseFloat(chargeUnit) || 0;
      const totalDayValue = parseFloat(formData.totalDay) || 0;
      const calculatedValue = perDayValue * totalDayValue;
      setChargeValue(calculatedValue.toString());
    } else if (chargeType === 'Type') {
      // For Type, chargeValue is directly entered, don't auto-calculate
    } else if ((chargeType === 'per km' || chargeType === 'per day') && (!chargeUnit || (chargeType === 'per km' && !formData.totalKm) || (chargeType === 'per day' && !formData.totalDay))) {
      setChargeValue('');
    }
  }, [chargeType, chargeUnit, formData.totalKm, formData.totalDay]);

  // Handle edit charge
  const handleEditCharge = (charge) => {
    // Determine if it's a custom type or standard type
    if (charge.chargeType === 'per km' || charge.chargeType === 'per day') {
      setChargeType(charge.chargeType);
      setCustomChargeType('');
    } else {
      // It's a custom charge type
      setChargeType('Type');
      setCustomChargeType(charge.chargeType);
    }
    setChargeUnit(charge.unit || '');
    setChargeValue(charge.amount.toString());
    setEditingChargeId(charge.id);
    
    // Set totalKm or totalDay if available (for per km/per day)
    if (charge.chargeType === 'per km') {
      setFormData(prev => ({ ...prev, totalKm: charge.totalKm ? charge.totalKm.toString() : '', totalDay: '' }));
    } else if (charge.chargeType === 'per day') {
      setFormData(prev => ({ ...prev, totalDay: charge.totalDay ? charge.totalDay.toString() : '', totalKm: '' }));
    } else {
      setFormData(prev => ({ ...prev, totalKm: '', totalDay: '' }));
    }
  };

  // Handle save charge (for both add and update)
  const handleSaveCharge = () => {
    const finalChargeType = chargeType === 'Type' ? customChargeType.trim() : chargeType;
    if (finalChargeType && chargeValue.trim()) {
      if (editingChargeId) {
        // Update existing charge
        setOtherCharges(prev => prev.map(charge => 
          charge.id === editingChargeId 
            ? {
                ...charge,
                chargeType: finalChargeType,
                unit: chargeUnit,
                amount: parseFloat(chargeValue) || 0,
                totalKm: chargeType === 'per km' ? formData.totalKm : charge.totalKm,
                totalDay: chargeType === 'per day' ? formData.totalDay : charge.totalDay
              }
            : charge
        ));
        setEditingChargeId(null);
      } else {
        // Add new charge
        const newCharge = {
          id: Date.now(),
          chargeType: finalChargeType,
          unit: chargeUnit,
          amount: parseFloat(chargeValue) || 0,
          totalKm: chargeType === 'per km' ? formData.totalKm : '',
          totalDay: chargeType === 'per day' ? formData.totalDay : ''
        };
        setOtherCharges(prev => [...prev, newCharge]);
      }
      // Reset form
      setChargeType('');
      setCustomChargeType('');
      setChargeUnit('');
      setChargeValue('');
      setFormData(prev => ({ ...prev, totalKm: '', totalDay: '' }));
    }
  };

  // Handle other charges (kept for backward compatibility, but now uses handleSaveCharge)
  const handleAddCharge = () => {
    handleSaveCharge();
  };

  const handleRemoveCharge = (id) => {
    setOtherCharges(prev => prev.filter(charge => charge.id !== id));
    // If removing the charge being edited, cancel edit mode
    if (editingChargeId === id) {
      setEditingChargeId(null);
      setChargeType('');
      setCustomChargeType('');
      setChargeUnit('');
      setChargeValue('');
      setFormData(prev => ({ ...prev, totalKm: '', totalDay: '' }));
    }
  };

  const handleResetCharges = () => {
    setChargeType('');
    setCustomChargeType('');
    setChargeUnit('');
    setChargeValue('');
    setEditingChargeId(null);
    setFormData(prev => ({ ...prev, totalKm: '', totalDay: '' }));
  };

  // Handle form actions
  const handleSave = async () => {
    try {
      const totalOtherCharges = otherCharges.reduce((sum, charge) => sum + (charge.amount || 0), 0);
      const payload = {
        vehicle_number: formData.vehicleNumber,
        driver_name: formData.driverName,
        driver_contact: formData.driverContactNumber,
        client_name: formData.clientName,
        client_address: formData.clientAddress,
        client_contact: formData.clientContact,
        consigner_name: formData.consignerName,
        consigner_address: formData.consignerAddress,
        consigner_contact: formData.consignerContact,
        consignee_name: formData.consigneeName,
        consignee_address: formData.consigneeAddress,
        consignee_contact: formData.consigneeContact,
        from_location: formData.consignerAddress,
        to_location: formData.consigneeAddress,
        vehicle_id: formData.vehicleId || null,
        driver_id: formData.driverId || null,
        agency_id: formData.agencyId || null,
        trip_id: formData.tripId || null,
        total_km: formData.totalKm || null,
        total_days: formData.totalDay || null,
        description: formData.description,
        subtotal: totalOtherCharges,
        total_amount: totalOtherCharges,
        bill_date: new Date().toISOString().slice(0, 10),
        items: otherCharges.map(c => ({
          charge_type: c.chargeType,
          unit: c.unit,
          quantity: c.totalKm || c.totalDay || 1,
          rate: c.unit ? parseFloat(c.unit) : 0,
          amount: c.amount
        }))
      };
      // Validate phone numbers (if present must be exactly 10 digits)
      const phoneChecks = [
        {label: 'Driver Contact', value: formData.driverContactNumber},
        {label: 'Client Contact', value: formData.clientContact},
        {label: 'Consigner Contact', value: formData.consignerContact},
        {label: 'Consignee Contact', value: formData.consigneeContact}
      ];
      for (const p of phoneChecks) {
        if (p.value && String(p.value).length !== 10) {
          alert(`${p.label} must be 10 digits`);
          return;
        }
      }

      let response;
      if (editingBillId) {
        response = await api.put(`/api/bills/${editingBillId}`, payload);
      } else {
        response = await api.post('/api/bills', payload);
      }
      const json = response.data;
      if (json && json.success) {
        // if editing, sync bill items (delete removed, update existing, create new)
        if (editingBillId) {
          try {
            const existingIds = (originalItemsRef.current || []).map(i => String(i.id));
            const currentItems = otherCharges || [];
            const currentIds = currentItems.map(c => c.id ? String(c.id) : null).filter(Boolean);
            const idsToDelete = existingIds.filter(id => !currentIds.includes(id));
            for (const id of idsToDelete) {
              try { await api.delete(`/api/bill-items/${id}`); } catch (e) { console.warn('delete item failed', id, e); }
            }
            for (const c of currentItems) {
              const body = {
                bill_id: editingBillId,
                charge_type: c.chargeType,
                unit: c.unit,
                quantity: c.totalKm || c.totalDay || 1,
                rate: c.unit ? parseFloat(c.unit) : 0,
                amount: c.amount,
                notes: c.notes || ''
              };
              if (c.id && existingIds.includes(String(c.id))) {
                try { await api.put(`/api/bill-items/${c.id}`, body); } catch (e) { console.warn('update item failed', c.id, e); }
              } else {
                try { await api.post('/api/bill-items', body); } catch (e) { console.warn('create item failed', e); }
              }
            }
          } catch (syncErr) {
            console.error('Bill items sync error', syncErr);
          }
          setEditingBillId(null);
          originalItemsRef.current = [];
        }
        await fetchBills({ agency_id: currentAgencyId });
        handleCloseModal();
        const createdId = (json.data && json.data.id) || (json.data && json.data.bill && json.data.bill.id) || null;
        setSuccessBillInfo({ id: createdId });
        setSuccessModalType(editingBillId ? 'update' : 'add');
        setShowSuccessModal(true);
      } else {
        console.error('Save failed', json);
        alert('Failed to save bill');
      }
    } catch (err) {
      console.error('Error saving bill', err);
      alert('Error saving bill');
    }
  };

  const handlePrint = async (billId) => {
    try {
      if (billId) {
        const response = await api.get(`/api/bills/${billId}/print`);
        const json = response.data;
        if (json && json.success && json.data) {
          const bill = json.data.bill || {};
          const items = json.data.items || [];

          const formatMoney = (v) => `₹${(Number(v)||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          const billNumber = bill.bill_number || bill.id || '';
          const billDate = bill.bill_date || bill.billDate || '';
          const vehicle = bill.vehicle_number || bill.vehicleNumber || 'Not specified';
          const driver = bill.driver_name || bill.driverName || 'Not specified';
          const driverContact = bill.driver_contact || bill.driverContact || bill.driverContactNumber || 'Not specified';
          const consigner = bill.consigner_name || bill.consignerName || bill.client_name || 'Not specified';
          const consignee = bill.consignee_name || bill.consigneeName || 'Not specified';
          const fromLocation = bill.from_location || bill.fromLocation || 'Not specified';
          const toLocation = bill.to_location || bill.toLocation || 'Not specified';

          const subtotal = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);
          const html = `
            <!doctype html>
            <html>
            <head>
              <meta charset="utf-8">
              <title>Invoice ${billNumber}</title>
              <style>
                .page { width:800px; margin:18px auto; padding:18px 28px; border:1px solid #ccc; box-sizing:border-box; font-family: "Helvetica Neue", Arial, sans-serif; color:#222; }
                .top-row { display:flex; justify-content:space-between; align-items:flex-start; }
                .brand { color:#0b5ed7; font-size:22px; font-weight:700; }
                .small { font-size:12px; color:#666; margin-top:4px; }
                .meta { text-align:right; font-size:12px; color:#444; line-height:1.2; }
                .section { margin-top:22px; }
                .two-col { display:flex; justify-content:space-between; gap:20px; }
                .col { width:48%; }
                .info-title { font-weight:700; color:#1f3b6e; margin-bottom:8px; font-size:13px; }
                .info-table td { padding:6px 0; vertical-align:top; font-size:13px; }
                .items { width:100%; border-collapse:collapse; margin-top:18px; font-size:13px; }
                .items th, .items td { border:1px solid #e6e6e6; padding:10px 12px; }
                .items th { background:#fafafa; font-weight:700; }
                .items td.amount { text-align:right; }
                .items td.unit { width:110px; text-align:center; }
                .totals { margin-top:12px; display:flex; justify-content:flex-end; }
                .totals .box { width:280px; }
                .totals .row { display:flex; justify-content:space-between; padding:6px 8px; border-top:1px solid #e6e6e6; }
                .totals .label { font-weight:700; }
                .footer-note { margin-top:30px; font-size:11px; color:#777; }
                @media print { .page { margin:0; border:none; } }
              </style>
            </head>
            <body>
              <div class="page">
                <div class="top-row">
                  <div>
                    <div class="brand">Nettaa Logistics Service</div>
                    <div class="small">YOUR TRUCKING COMPANY</div>
                    <div class="company-details" style="margin-top:8px; font-size:12px; color:#444; line-height:1.4;">
                      <div class="company-line" style="margin-top:4px;">Truck Booking App, Truck Rental, Logistics App</div>
                      <div class="company-line" style="margin-top:4px;">Packers and Movers, India</div>
                      <div class="company-line" style="margin-top:10px;">E-mail : info@nettaalogistics.com</div>
                      <div class="company-line" style="margin-top:4px;">Website : www.nettaalogistics.com</div>
                    </div>
                  </div>
                  <div class="meta">
                    <div>${billDate}</div>
                    <div>Carrier's Rate Confirmation - ${billNumber}</div>
                  </div>
                </div>

                <div class="section">
                  <div class="info-title">CARRIER INFORMATION</div>
                  <table class="info-table">
                    <tr><td>Vehicle Number:</td><td>${vehicle}</td></tr>
                    <tr><td>Driver Name:</td><td>${driver}</td></tr>
                    <tr><td>Driver Contact:</td><td>${driverContact}</td></tr>
                  </table>
                </div>

                <div class="section two-col">
                  <div class="col">
                    <div class="info-title">PICKUP INFORMATION</div>
                    <table class="info-table">
                      <tr><td>Consigner Name:</td><td>${consigner}</td></tr>
                      <tr><td>From Address:</td><td>${fromLocation}</td></tr>
                    </table>
                  </div>
                  <div class="col">
                    <div class="info-title">DELIVERY INFORMATION</div>
                    <table class="info-table">
                      <tr><td>Consignee Name:</td><td>${consignee}</td></tr>
                      <tr><td>To Address:</td><td>${toLocation}</td></tr>
                    </table>
                  </div>
                </div>

                <table class="items">
                  <thead>
                    <tr>
                      <th>CHARGE TYPE</th>
                      <th class="unit">UNIT</th>
                      <th class="amount">AMOUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${items.map(it => `
                      <tr>
                        <td>${it.charge_type || it.item_description || '-'}</td>
                        <td class="unit">${it.unit || '-'}</td>
                        <td class="amount">${formatMoney(it.amount)}</td>
                      </tr>
                    `).join('')}
                  </tbody>
                </table>

                <div class="totals">
                  <div class="box">
                    <div class="row"><div class="label">TOTAL CHARGE</div><div>${formatMoney(subtotal)}</div></div>
                  </div>
                </div>

                <div class="footer-note">Generated by Nettaa Logistics Service</div>
              </div>
            </body>
            </html>
          `;
          const win = window.open('', '_blank');
          win.document.write(html);
          win.document.close();
          win.onload = () => setTimeout(() => win.print(), 300);
          return;
        } else {
          alert('Failed to fetch printable bill');
          return;
        }
    }

      // Fallback: print current form data (unsaved)
      const grandTotal = otherCharges.reduce((sum, charge) => sum + (charge.amount || 0), 0);
      const chargesForPrint = otherCharges;
      const billIdLocal = `BILL-${String(bills.length + 1).padStart(3, '0')}`;
      const htmlContent = `<!doctype html><html><head><meta charset="utf-8"><title>Bill ${billIdLocal}</title>
        <style>body{font-family:Arial,Helvetica,sans-serif;padding:20px}table{width:100%;border-collapse:collapse}th,td{padding:8px;border:1px solid #ddd;text-align:left}</style>
        </head><body>
        <h2>Bill: ${billIdLocal}</h2>
        <p><strong>From:</strong> ${formData.fromLocation || ''} &nbsp; <strong>To:</strong> ${formData.toLocation || ''}</p>
        <p><strong>Vehicle:</strong> ${formData.vehicleNumber || ''} &nbsp; <strong>Driver:</strong> ${formData.driverName || ''}</p>
        <table><thead><tr><th>Charge</th><th>Unit</th><th>Amount</th></tr></thead><tbody>
        ${chargesForPrint.map(c => `<tr><td>${c.chargeType}</td><td>${c.unit||''}</td><td>₹${(c.amount||0).toLocaleString('en-IN')}</td></tr>`).join('')}
        </tbody></table>
        <h3>Total: ₹${grandTotal.toLocaleString('en-IN')}</h3>
        </body></html>`;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
      printWindow.onload = () => setTimeout(() => printWindow.print(), 250);
    } catch (err) {
      console.error('Print error', err);
      alert('Unable to print bill');
    }
  };

  const handleEdit = async (billId) => {
    try {
      if (!billId) return alert('Invalid bill id for edit');
      const response = await api.get(`/api/bills/${billId}/print`);
      const json = response.data;
      if (json && json.success && json.data) {
        const bill = json.data.bill || {};
        const items = json.data.items || [];
        setFormData({
          vehicleNumber: bill.vehicle_number || bill.vehicleNumber || '',
          vehicleId: bill.vehicle_id || bill.vehicleId || null,
          driverName: bill.driver_name || bill.driverName || '',
          driverId: bill.driver_id || bill.driverId || null,
          driverContactNumber: bill.driver_contact || bill.driverContact || '',
          clientName: bill.client_name || bill.clientName || '',
          clientAddress: bill.client_address || bill.clientAddress || '',
          clientContact: bill.client_contact || bill.clientContact || '',
          consignerName: bill.consigner_name || bill.consignerName || '',
          consignerAddress: bill.consigner_address || bill.consignerAddress || '',
          consignerContact: bill.consigner_contact || bill.consignerContact || '',
          consigneeName: bill.consignee_name || bill.consigneeName || '',
          consigneeAddress: bill.consignee_address || bill.consigneeAddress || '',
          consigneeContact: bill.consignee_contact || bill.consigneeContact || '',
          fromLocation: bill.from_location || bill.fromLocation || '',
          toLocation: bill.to_location || bill.toLocation || '',
          distance: bill.distance_km || 0,
          agencyId: bill.agency_id || bill.agencyId || null,
          tripId: bill.trip_id || bill.tripId || null,
          description: bill.description || '',
          totalKm: bill.total_km || '',
          totalDay: bill.total_days || ''
        });
        const mapped = items.map((it, idx) => ({
          id: it.id || Date.now() + idx,
          chargeType: it.charge_type || it.custom_type || '',
          unit: it.unit || '',
          amount: Number(it.amount) || 0,
          totalKm: it.quantity || it.totalKm || '',
          totalDay: it.quantity || it.totalDay || ''
        }));
        setOtherCharges(mapped);
        // store original items for sync on save
        originalItemsRef.current = items || [];
        setEditingBillId(billId);
        setShowBillGenerationModal(true);
      } else {
        alert('Failed to fetch bill for edit');
      }
    } catch (err) {
      console.error('Edit fetch error', err);
      alert('Unable to load bill for edit');
    }
  };

  const handleDelete = (billId) => {
    if (!billId) return alert('Invalid bill id');
    setBillToDelete(billId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setBillToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    const billId = billToDelete;
    if (!billId) {
      setShowDeleteConfirm(false);
      return;
    }
    try {
      const res = await api.delete(`/api/bills/${billId}`);
      if (res.data && res.data.success) {
        await fetchBills({ agency_id: currentAgencyId });
        setDeletedBillInfo({ id: billId });
        setShowDeleteConfirm(false);
        setBillToDelete(null);
        setShowDeleteSuccess(true);
      } else {
        console.error('Delete failed', res.data);
        alert('Failed to delete bill');
      }
    } catch (err) {
      console.error('Delete error', err);
      alert('Error deleting bill');
    }
  };

  const handleCloseModal = () => {
    setFormData({
      vehicleNumber: '',
      driverName: '',
      driverContactNumber: '',
      clientName: '',
      clientAddress: '',
      clientContact: '',
      consignerName: '',
      consignerAddress: '',
      consignerContact: '',
      consigneeName: '',
      consigneeAddress: '',
      consigneeContact: '',
      fromLocation: '',
      toLocation: '',
      distance: 0,
      description: '',
      totalKm: '',
      totalDay: ''
    });
    setOtherCharges([]);
    setChargeType('');
    setCustomChargeType('');
    setChargeUnit('');
    setChargeValue('');
    setFromLocationSearch('');
    setToLocationSearch('');
    setFromLocationSuggestionsOpen(false);
    setToLocationSuggestionsOpen(false);
    setShowBillGenerationModal(false);
    setEditingBillId(null);
  };

  const handleReset = () => {
    setFormData({
      vehicleNumber: '',
      driverName: '',
      driverContactNumber: '',
      clientName: '',
      clientAddress: '',
      clientContact: '',
      consignerName: '',
      consignerAddress: '',
      consignerContact: '',
      consigneeName: '',
      consigneeAddress: '',
      consigneeContact: '',
      fromLocation: '',
      toLocation: '',
      distance: 0,
      description: '',
      totalKm: '',
      totalDay: ''
    });
    setOtherCharges([]);
    setChargeType('');
    setChargeValue('');
  };

  // Calculate total amount
  const totalOtherChargesAmount = otherCharges.reduce((sum, charge) => sum + charge.amount, 0);
  const grandTotal = totalOtherChargesAmount;

  return (
    <div className="bill-generation-container">
      {/* Header */}
      <div className="bill-header">
        <div className="filter-group">
          <div className="filter-wrapper">
            <input
              type="date"
              className="filter-input"
              placeholder="From Date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div className="filter-wrapper">
            <input
              type="date"
              className="filter-input"
              placeholder="To Date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
        </div>
        <button className="bill-generation-btn" onClick={() => setShowBillGenerationModal(true)}>
          Bill Generation
        </button>
      </div>

      {/* Bill Generation Modal */}
      {showBillGenerationModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="bill-generation-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Bill Generation</h2>
              <button className="modal-close-btn" onClick={handleCloseModal}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <div className="modal-body">
      {/* Form Section */}
      <div className="bill-form-section">
        {/* Pickup and Vehicle Details */}
        <div className="form-section">
          <h2 className="section-title">Pickup and Vehicle Details</h2>
          
          <div className="form-grid form-grid-always-three">
            {/* Row 1 */}
            <div className="form-field">
              <label>Vehicle Number</label>
              <div className="dropdown-wrapper" ref={vehicleNumberDropdownRef}>
                <button
                  className="dropdown-toggle"
                  onClick={() => {
                    const opening = !vehicleNumberDropdownOpen;
                    setVehicleNumberDropdownOpen(opening);
                    if (opening) fetchVehicles(vehicleSearch);
                  }}
                >
                  <span>{formData.vehicleNumber || 'Select vehicle number'}</span>
                  <svg className="dropdown-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {vehicleNumberDropdownOpen && (
                  <div className="dropdown-menu">
                    <div style={{ padding: '8px 12px' }}>
                      <input
                        type="text"
                        placeholder="Search vehicle..."
                        value={vehicleSearch}
                        onChange={(e) => {
                          const v = e.target.value;
                          setVehicleSearch(v);
                          if (vehicleDebounceRef.current) clearTimeout(vehicleDebounceRef.current);
                          vehicleDebounceRef.current = setTimeout(() => fetchVehicles(v), 300);
                        }}
                        className="dropdown-search-input"
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                      />
                    </div>
                    <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                      {vehicleNumberOptions
                        .filter(opt => vehicleSearch.trim() === '' || (opt || '').toLowerCase().includes(vehicleSearch.toLowerCase()))
                        .map(option => {
                      const vehicleObj = vehicles.find(v => v.vehicle_number === option);
                      return (
                      <div
                        key={option}
                        className={`dropdown-option ${formData.vehicleNumber === option ? 'selected' : ''}`}
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            vehicleNumber: option,
                            vehicleId: vehicleObj ? vehicleObj.id || vehicleObj.vehicle_id || null : null,
                            agencyId: vehicleObj ? (vehicleObj.agency_id || vehicleObj.agencyId || prev.agencyId) : prev.agencyId,
                            tripId: vehicleObj ? (vehicleObj.trip_id || vehicleObj.tripId || prev.tripId) : prev.tripId,
                            driverName: (vehicleObj && (vehicleObj.assigned_driver_name || vehicleObj.driver_name)) || prev.driverName,
                            driverId: vehicleObj ? (vehicleObj.assigned_driver_id || vehicleObj.assigned_driver || vehicleObj.driver_id || prev.driverId) : prev.driverId
                          }));
                          setVehicleNumberDropdownOpen(false);
                              setVehicleSearch('');
                        }}
                      >
                        {option}
                      </div>
                      )
                    })}
                      {vehicleNumberOptions.filter(opt => !vehicleSearch || (opt || '').toLowerCase().includes(vehicleSearch.toLowerCase())).length === 0 && (
                        <div className="dropdown-no-results" style={{ padding: '10px 16px', color: '#6b7280' }}>No vehicles</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="form-field">
              <label>Driver Name</label>
              <div className="dropdown-wrapper" ref={driverNameDropdownRef}>
                <button
                  className="dropdown-toggle"
                  onClick={() => {
                    const opening = !driverNameDropdownOpen;
                    setDriverNameDropdownOpen(opening);
                    if (opening) fetchDrivers(driverSearch);
                  }}
                >
                  <span>{formData.driverName || 'Select driver'}</span>
                  <svg className="dropdown-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {driverNameDropdownOpen && (
                  <div className="dropdown-menu">
                    <div style={{ padding: '8px 12px' }}>
                      <input
                        type="text"
                        placeholder="Search driver..."
                        value={driverSearch}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDriverSearch(v);
                          if (driverDebounceRef.current) clearTimeout(driverDebounceRef.current);
                          driverDebounceRef.current = setTimeout(() => fetchDrivers(v), 300);
                        }}
                        className="dropdown-search-input"
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                      />
                    </div>
                    <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                      {allDrivers
                        .filter(d => driverSearch.trim() === '' || (d || '').toLowerCase().includes(driverSearch.toLowerCase()))
                        .map(driver => {
                      const driverObj = drivers.find(d => (d.full_name || d.name || '') === driver);
                      return (
                      <div
                        key={driver}
                        className={`dropdown-option ${formData.driverName === driver ? 'selected' : ''}`}
                        onClick={() => {
                          setFormData(prev => ({ 
                            ...prev, 
                            driverName: driver,
                            driverId: driverObj ? driverObj.id || driverObj.driver_id || null : null,
                            driverContactNumber: driverObj ? (driverObj.mobile_number || driverObj.contact || driverObj.phone || '') : prev.driverContactNumber
                          }));
                          setDriverNameDropdownOpen(false);
                              setDriverSearch('');
                        }}
                      >
                        {driver}
                      </div>
                      )
                    })}
                      {allDrivers.filter(d => !driverSearch || (d || '').toLowerCase().includes(driverSearch.toLowerCase())).length === 0 && (
                        <div className="dropdown-no-results" style={{ padding: '10px 16px', color: '#6b7280' }}>No drivers</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="form-field">
              <label>Driver Contact Number</label>
              <input
                type="text"
                className="form-input readonly"
                placeholder="Auto-filled from vehicle"
                value={formData.driverContactNumber}
                readOnly
              />
            </div>

          </div>
        </div>

        {/* Consigner Details */}
        <div className="form-section">
          <h2 className="section-title">Consigner Details</h2>
          <div className="form-grid form-grid-always-three">
            <div className="form-field">
              <label>Consigner Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter consigner name"
                value={formData.consignerName}
                onChange={(e) => setFormData(prev => ({ ...prev, consignerName: e.target.value }))}
              />
            </div>
            <div className="form-field">
              <label>Consigner Contact</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter contact number"
                inputMode="numeric"
                maxLength="10"
                value={formData.consignerContact}
                onChange={(e) => setFormData(prev => ({ ...prev, consignerContact: sanitizePhone(e.target.value) }))}
              />
            </div>
            <div className="form-field">
              <label>Consigner Address</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter consigner address..."
                value={formData.consignerAddress}
                onChange={(e) => setFormData(prev => ({ ...prev, consignerAddress: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Consignee Details */}
        <div className="form-section">
          <h2 className="section-title">Consignee Details</h2>
          <div className="form-grid form-grid-always-three">
            <div className="form-field">
              <label>Consignee Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter consignee name"
                value={formData.consigneeName}
                onChange={(e) => setFormData(prev => ({ ...prev, consigneeName: e.target.value }))}
              />
            </div>
            <div className="form-field">
              <label>Consignee Contact</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter contact number"
                inputMode="numeric"
                maxLength="10"
                value={formData.consigneeContact}
                onChange={(e) => setFormData(prev => ({ ...prev, consigneeContact: sanitizePhone(e.target.value) }))}
              />
            </div>
            <div className="form-field">
              <label>Consignee Address</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter consignee address..."
                value={formData.consigneeAddress}
                onChange={(e) => setFormData(prev => ({ ...prev, consigneeAddress: e.target.value }))}
              />
            </div>
          </div>
        </div>

        

        {/* Charges */}
        <div className="form-section">
          <h2 className="section-title">Charges</h2>
          
          <div className="other-charges-inputs">
            <div className="charge-input-group">
              <label>Charge Type</label>
              <div className="dropdown-wrapper" ref={chargeTypeDropdownRef}>
                <button
                  className="dropdown-toggle"
                  onClick={() => {
                    setChargeTypeDropdownOpen(!chargeTypeDropdownOpen);
                  }}
                >
                  <span>{chargeType || 'Select charge type'}</span>
                  <svg className="dropdown-arrow" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                {chargeTypeDropdownOpen && (
                  <div className="dropdown-menu">
                    {['per km', 'per day', 'Type'].map(option => (
                      <div
                        key={option}
                        className={`dropdown-option ${chargeType === option ? 'selected' : ''}`}
                        onClick={() => handleChargeTypeSelect(option)}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {chargeType === 'Type' && (
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter custom charge type"
                  value={customChargeType}
                  onChange={(e) => setCustomChargeType(e.target.value)}
                  style={{ marginTop: '8px' }}
                />
              )}
            </div>

            {/* Show Unit field only for per km and per day */}
            {(chargeType === 'per km' || chargeType === 'per day') && (
              <>
                <div className="charge-input-group">
                  <label>{chargeType === 'per km' ? 'per km' : chargeType === 'per day' ? 'per day' : 'Unit'}</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder={
                      chargeType === 'per km' ? 'Enter kilometers' :
                      'Enter days'
                    }
                    value={chargeUnit}
                    onChange={(e) => setChargeUnit(e.target.value)}
                  />
                </div>
                {chargeType === 'per km' && (
                  <div className="charge-input-group">
                    <label>Total km</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Enter total km"
                      value={formData.totalKm}
                      onChange={(e) => setFormData(prev => ({ ...prev, totalKm: e.target.value }))}
                    />
                  </div>
                )}
                {chargeType === 'per day' && (
                  <div className="charge-input-group">
                    <label>Total day</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="Enter total days"
                      value={formData.totalDay}
                      onChange={(e) => setFormData(prev => ({ ...prev, totalDay: e.target.value }))}
                    />
                  </div>
                )}
              </>
            )}

            <div className="charge-input-group">
              <label>Charge Value</label>
              <input
                type="text"
                className={`form-input ${(chargeType === 'per km' || chargeType === 'per day') ? 'readonly' : ''}`}
                placeholder="Enter amount"
                value={chargeValue}
                onChange={(e) => {
                  // Only allow manual entry for Type, auto-calculated for per km/per day
                  if (chargeType !== 'per km' && chargeType !== 'per day') {
                    setChargeValue(e.target.value);
                  }
                }}
                readOnly={chargeType === 'per km' || chargeType === 'per day'}
              />
            </div>

            <div className="charge-actions">
              {editingChargeId ? (
                <button className="btn-add" onClick={handleSaveCharge}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Save
                </button>
              ) : (
                <>
                  <button className="btn-add" onClick={handleAddCharge}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Add
                  </button>
                  <button className="btn-reset" onClick={handleResetCharges}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 8C2 4.68629 4.68629 2 8 2C11.3137 2 14 4.68629 14 8C14 11.3137 11.3137 14 8 14M8 14L6 12M8 14L10 12M8 8V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Reset
                  </button>
                </>
              )}
            </div>
          </div>

          {otherCharges.length > 0 && (
            <div className="other-charges-table">
              <table>
                <thead>
                  <tr>
                    <th>CHARGE TYPE</th>
                    <th>UNIT</th>
                    <th>AMOUNT</th>
                    <th>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {otherCharges.map(charge => (
                    <tr key={charge.id}>
                      <td>{charge.chargeType}</td>
                      <td>{charge.unit || '-'}</td>
                      <td className="amount-cell">₹{charge.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="action-cell">
                        <div className="action-buttons">
                          <button
                            className="action-btn edit-btn"
                            onClick={() => handleEditCharge(charge)}
                            title="Edit"
                          >
                            Edit
                          </button>
                          <button
                            className="action-btn remove-btn"
                            onClick={() => handleRemoveCharge(charge.id)}
                            title="Close"
                          >
                            Close
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Description */}
          <div className="form-section">
            <div className="form-field" style={{ width: '100%' }}>
              <label>Description</label>
              <textarea
                className="form-input"
                placeholder="Enter description or notes..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows="4"
                style={{ 
                  resize: 'vertical',
                  minHeight: '100px',
                  fontFamily: 'inherit'
                }}
              />
            </div>
          </div>

          {/* Total Charge */}
          <div className="total-charge-section">
            <div className="total-charge-field">
              <label>Total Charge</label>
              <input
                type="text"
                className="form-input readonly total-charge-input"
                value={`₹${grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                readOnly
              />
            </div>
          </div>
        </div>

        {/* Form Footer */}
        <div className="form-footer">
          <button className="btn-secondary" onClick={handleCloseModal}>Close</button>
          <button className="btn-secondary" onClick={() => handlePrint(editingBillId)}>Print</button>
          <button className="btn-primary" onClick={handleSave}>Save</button>
        </div>
      </div>
            </div>
          </div>
        </div>
      )}

      {/* Show Entries and Search */}
      <div className="show-entries-container">
        <div className="show-entries-wrapper">
          <div className="show-entries-left">
            <span className="show-entries-label">Show</span>
            <div className="filter-wrapper" ref={showEntriesDropdownRef}>
              <button
                className="show-entries-btn"
                onClick={() => setShowEntriesDropdownOpen(!showEntriesDropdownOpen)}
              >
                <span>{itemsPerPage}</span>
                <span>▼</span>
              </button>
              {showEntriesDropdownOpen && (
                <div className="filter-dropdown">
                  {[10, 25, 50, 100].map(num => (
                    <button
                      key={num}
                      className={`filter-option ${itemsPerPage === num ? 'selected' : ''}`}
                      onClick={() => {
                        setItemsPerPage(num);
                        setCurrentPage(1);
                        setShowEntriesDropdownOpen(false);
                      }}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <span className="show-entries-label">entries</span>
          </div>
          <div className="search-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder="Search bills..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="search-icon">🔍</span>
          </div>
        </div>
      </div>

      {/* Bills Table */}
      <div className="bills-table-section">
        <div className="table-container">
          <table className="bills-table">
            <thead>
              <tr>
                <th>Bill ID</th>
                <th>Bill Date</th>
                <th>From Location</th>
                <th>To Location</th>
                <th>Vehicle Number</th>
                <th>Driver Name</th>
                <th>Total Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBills.length === 0 ? (
                <tr>
                  <td colSpan="9" className="no-data">
                    No bills found
                  </td>
                </tr>
              ) : (
                paginatedBills.map(bill => {
                  const printId = bill.raw && (typeof bill.raw.id !== 'undefined') ? bill.raw.id : (Number(bill.id) || undefined);
                  return (
                  <tr key={bill.id}>
                    <td>{bill.id}</td>
                    <td>{bill.billDate}</td>
                    <td>{bill.fromLocation}</td>
                    <td>{bill.toLocation}</td>
                    <td>{bill.vehicleNumber}</td>
                    <td>{bill.driverName}</td>
                    <td>₹{(bill.totalAmount || 0).toLocaleString('en-IN')}</td>
                    <td>
                      <span className={`status-badge ${bill.status.toLowerCase()}`}>
                        {bill.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn edit-btn" title="Edit" onClick={() => handleEdit(printId)}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 21h4l11-11a2 2 0 0 0-4-4L4 17v4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </button>
                        <button className="action-btn delete-btn" title="Delete" onClick={() => handleDelete(printId)} style={{ marginLeft: 8 }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M8 6v14a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6M10 11v6M14 11v6M9 6V4h6v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </button>
                        <button className="action-btn print-btn" title="Print" onClick={() => handlePrint(printId)} style={{ marginLeft: 8 }}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 4H12V2H4V4ZM4 12H12V10H4V12ZM2 14H14V8H2V14ZM14 6H2V4H14V6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="table-footer">
        <div className="pagination-info">
          Showing {paginatedBills.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{' '}
          {Math.min(currentPage * itemsPerPage, filteredBills.length)} of {filteredBills.length} entries
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            ←
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            →
          </button>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && billToDelete && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="delete-vehicle-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-vehicle-header">
              <div className="delete-warning-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="delete-vehicle-title">Confirm Action</h3>
            </div>
            <div className="delete-vehicle-body">
              <p className="delete-vehicle-message">
                Are you sure you want to delete this bill? This action cannot be undone.
              </p>
              <div className="delete-vehicle-details">
                <div className="delete-detail-row">
                  <span className="delete-detail-label">Bill ID:</span>
                  <span className="delete-detail-value">{billToDelete}</span>
                </div>
              </div>
            </div>
            <div className="delete-vehicle-footer">
              <button className="delete-vehicle-cancel-btn" onClick={handleDeleteCancel}>
                Cancel
              </button>
              <button className="delete-vehicle-delete-btn" onClick={handleDeleteConfirm}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }}>
                  <path d="M6.5 4V3.5C6.5 2.94772 6.94772 2.5 7.5 2.5H8.5C9.05228 2.5 9.5 2.94772 9.5 3.5V4M12 4H4M11 4V13.5C11 14.0523 10.5523 14.5 10 14.5H6C5.44772 14.5 5 14.0523 5 13.5V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M6.5 7V11.5M9.5 7V11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Modal */}
      {showDeleteSuccess && deletedBillInfo && (
        <div className="success-modal-overlay" onClick={() => setShowDeleteSuccess(false)}>
          <div className="delete-success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="delete-success-header">
              <div className="delete-success-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="delete-success-title">Success</h3>
              <button className="delete-success-close" onClick={() => setShowDeleteSuccess(false)} style={{ background: 'none', border: 'none', fontSize: '1.2rem', color: '#999', cursor: 'pointer', padding: '0', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✖</button>
            </div>
            <div className="delete-success-body">
              <p className="delete-success-message">Bill successfully deleted</p>
              <div className="delete-success-details">
                <div className="delete-success-detail-row">
                  <span className="delete-success-detail-label">Bill ID:</span>
                  <span className="delete-success-detail-value">{deletedBillInfo.id}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal (create/update) */}
      {showSuccessModal && (
        <div className="success-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#10B981"/>
                <path d="M16 24L21 29L32 18" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="success-title">Success!</h2>
            <p className="success-message">
              {successModalType === 'add' ? 'Bill has been created successfully.' : 'Bill has been updated successfully.'}
            </p>
            {successBillInfo && (
              <div className="success-id-badge">{successBillInfo.id}</div>
            )}
            <button className="success-ok-btn" onClick={() => setShowSuccessModal(false)}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyBillGeneration;
